import { FC, useContext, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/system';
import MediumTypography from '../../components/formlib/MediumTypography';
import { ReactComponent as DownArrow } from '../../assets/images/downarrowcolored.svg';
import { ReactComponent as UpArrow } from '../../assets/images/uparrowcolored.svg';
import { ReactComponent as InfoIcon } from '../../assets/images/infoIcon.svg';
import InformationModal from './InformationModal';
import TransitionPreparationData from './IFSP/TransitionPreparation.json';
import SimpleTextAndTextArea from '../../components/forms/SimpleTextAndTextArea';
import IfspContext from './ifspContextApi/IfspContex';
import dayjs, { Dayjs } from 'dayjs';
import DatePickerComponent from '../../components/formlib/DatePickerComponent';
import { datePickerMinDateMaxDateValidate } from '../../utils/dateUtil';

interface TransitionPreparationProps {
  info: boolean;
  open: boolean;
  selectedLanguage: string;
  onClose: (open: boolean) => void;
  closeInfo: (info: boolean) => void;
  onOpen: (open: boolean) => void;
  openInfo: (info: boolean) => void;
  hasEditPermission: boolean;
}
const TransitionPreparation: FC<TransitionPreparationProps> = ({
  open,
  onOpen,
  onClose,
  info,
  openInfo,
  closeInfo,
  selectedLanguage,
  hasEditPermission,
}) => {
  const { ifsp, updateIfspContext } = useContext(IfspContext);
  const [transistionPrepQuestion, setTransistionPrepQuestion] = useState<
    { commentId: number; comments: string }[]
  >(
    TransitionPreparationData.Questions.map((_, index) => ({
      commentId: index,
      comments: '',
    })),
  );

  const [transitionPreparation, setTransitionPreparation] = useState<{
    changesComingUp: string;
    preparationHelp: string;
    supports: string;
    communityOpportunities: string;
    serviceChangesNeeded: string;
    transitionPreparationDate: string | null | Dayjs;
  }>({
    changesComingUp: '',
    preparationHelp: '',
    supports: '',
    communityOpportunities: '',
    serviceChangesNeeded: '',
    transitionPreparationDate: null,
  });

  const permission = hasEditPermission && selectedLanguage === 'en';
  const parentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTransistionPrepQuestion((prev) => {
      const temp = [...prev];
      temp[0].comments =
        ifsp.ifspTeam.forms.transitionPreparation.changesComingUp;
      temp[1].comments =
        ifsp.ifspTeam.forms.transitionPreparation.preparationHelp;
      temp[2].comments = ifsp.ifspTeam.forms.transitionPreparation.supports;
      temp[3].comments =
        ifsp.ifspTeam.forms.transitionPreparation.communityOpportunities;
      temp[4].comments =
        ifsp.ifspTeam.forms.transitionPreparation.serviceChangesNeeded;
      return temp;
    });
  }, [ifsp]);

  return (
    <Box component="main">
      {info && (
        <InformationModal
          open={info}
          titleLabel="IFSP.Information"
          titleDefaultLabel="Information"
          buttonLabel="IFSP.InfoOkButton"
          buttonDefaultLabel="Ok"
          onClose={() => {
            closeInfo(info);
          }}
          info={TransitionPreparationData.info}
        />
      )}
      <Box
        component="section"
        className="p-lg borderRadius4"
        sx={{
          border: '1px solid #00C6B8',
          backgroundColor: '#ECF9F8',
        }}
      >
        <Box component="div" className="flex__justify__space-between">
          {open ? (
            <Box component="div" className="flex__justify__space-between">
              <MediumTypography
                labelid="IFSP.session8"
                defaultlabel="Transition Preparation"
                fontSize="18px"
                fontweight={600}
                textColor="#2A4241"
                className="pr-md"
              />
              <InfoIcon
                className="cursorPointer"
                onClick={() => {
                  openInfo(info);
                }}
              />
            </Box>
          ) : (
            <MediumTypography
              labelid="IFSP.session8"
              defaultlabel="Transition Preparation"
              fontSize="18px"
              fontweight={600}
              textColor="#2A4241"
            />
          )}
          {!open ? (
            <DownArrow
              className="cursorPointer"
              style={{ color: '#008C82' }}
              onClick={() => {
                onOpen(open);
              }}
            />
          ) : (
            <UpArrow
              className="cursorPointer"
              onClick={() => {
                onClose(open);
              }}
            />
          )}
        </Box>
        {open && (
          <Box component="div" className="mt-lg">
            <Box
              ref={parentRef}
              style={{
                pointerEvents: permission ? 'auto' : 'none',
                opacity: permission ? 'unset' : '0.8',
              }}
            >
              <Box className="width300px">
                <DatePickerComponent
                  className="width200px bg__white"
                  labelid={'Date'}
                  defaultlabelid={'Date'}
                  value={
                    ifsp.ifspTeam.forms.transitionPreparation
                      .transitionPreparationDate
                      ? dayjs(
                          ifsp.ifspTeam.forms.transitionPreparation
                            .transitionPreparationDate,
                          'MM/DD/YYYY',
                        )
                      : null
                  }
                  handlechange={(value: Dayjs | null) => {
                    // Update local state (keep Dayjs for UI)
                    setTransitionPreparation({
                      ...transitionPreparation,
                      transitionPreparationDate: value,
                    });

                    // Update IFSP context (store formatted string or null for persistence)
                    updateIfspContext({
                      ...ifsp,
                      ifspTeam: {
                        ...ifsp.ifspTeam,
                        forms: {
                          ...ifsp.ifspTeam.forms,
                          transitionPreparation: {
                            ...ifsp.ifspTeam.forms.transitionPreparation,
                            transitionPreparationDate: value
                              ? value.format('MM/DD/YYYY')
                              : null,
                          },
                        },
                      },
                    });
                  }}
                />
                {datePickerMinDateMaxDateValidate(
                  ifsp.ifspTeam.forms.transitionPreparation
                    .transitionPreparationDate,
                ) && (
                  <Box className="loginError">
                    <MediumTypography
                      labelid="datePickerMinDateMaxDateValidate"
                      defaultlabel="Enter a date between 01/01/1900 and 01/01/2100"
                      className="errorText-md"
                    />
                  </Box>
                )}
              </Box>
              <SimpleTextAndTextArea
                placeholder="Enter your comments"
                questions={TransitionPreparationData.Questions}
                rows={5}
                customCommentStyles={{
                  backgroundColor: '#ffffff',
                  minWidth: '840px',
                  width: '840px',
                }}
                maxLength={5000}
                Value={transistionPrepQuestion}
                handlechange={(value: {
                  commentId: number;
                  comments: string;
                }) => {
                  const updatedComments = [...transistionPrepQuestion];
                  updatedComments[value.commentId].comments = value.comments;
                  setTransistionPrepQuestion(updatedComments);
                  const updatedData = {
                    ...transitionPreparation,
                    changesComingUp: updatedComments[0].comments,
                    preparationHelp: updatedComments[1].comments,
                    supports: updatedComments[2].comments,
                    communityOpportunities: updatedComments[3].comments,
                    serviceChangesNeeded: updatedComments[4].comments,
                  };
                  updateIfspContext({
                    ...ifsp,
                    ifspTeam: {
                      ...ifsp.ifspTeam,
                      forms: {
                        ...ifsp.ifspTeam.forms,
                        transitionPreparation: {
                          ...ifsp.ifspTeam.forms.transitionPreparation,
                          changesComingUp: updatedComments[0].comments,
                          preparationHelp: updatedComments[1].comments,
                          supports: updatedComments[2].comments,
                          communityOpportunities: updatedComments[3].comments,
                          serviceChangesNeeded: updatedComments[4].comments,
                        },
                      },
                    },
                  });
                  setTransitionPreparation(updatedData);
                }}
              />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TransitionPreparation;
